import React, { useEffect, useMemo, useRef, useState } from "react";
import { Spinner } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { setPageNameHeader } from "src/redux/actions/header";
import { FirebaseDataService, GetReportingDataResponse } from "src/services/Firebase/data";
import { Content, colors } from "src/styles";
import styled from "styled-components";
import * as d3 from "d3";

// ─── Static Q1 2026 Data ───────────────────────────────────────────────────────

// ─── Colour palette for service type categories ───────────────────────────
// Named map for consistent colouring regardless of sort order.
// Fallback colours are used for any category not explicitly listed.
const CATEGORY_COLORS: Record<string, string> = {
  "Plumbing/Washroom": "#378ADD",
  "Temperature/HVAC": "#FF7A00",
  "Electrical/Lighting": "#F5C518",
  "Doors/Access": "#8B5CF6",
  "Cleaning/Janitorial": "#639922",
  "Structural/Repair": "#888780",
  "Fuel/Energy Equipment": "#EF4444",
  "Events/Room Setup": "#97C459",
  "Safety/Security": "#E24B4A",
  "Moving & Logistics": "#60A5FA",
  "Pest Control": "#1D9E75",
  "Waste/Bin Services": "#78716C",
  "Snow/Ice Removal": "#7DD3FC",
  "Safety/Compliance Inspections": "#DC2626",
  "Furniture & Equipment": "#A78BFA",
  "Signage & Displays": "#6366F1",
  "Kitchen/Appliances": "#EA580C",
  Parking: "#4B5563",
  "General Maintenance": "#B45309",
  "Grounds/Exterior": "#16A34A",
  "IT/Telecom": "#0EA5E9",
  "Supplies & Consumables": "#D97706",
  "Noise/Vibration": "#EC4899",
  "Elevator/Lift": "#9CA3AF",
  "Environmental Assessment": "#C0DD97",
  Other: "#BBBBBB",
  Unknown: "#DDDDDD",
};
const FALLBACK_COLORS = [
  "#639922",
  "#378ADD",
  "#FF7A00",
  "#8B5CF6",
  "#E24B4A",
  "#1D9E75",
  "#F5C518",
  "#888780",
  "#97C459",
  "#60A5FA",
];
const getCategoryColor = (name: string, index: number): string =>
  CATEGORY_COLORS[name] ?? FALLBACK_COLORS[index % FALLBACK_COLORS.length];

const SURVEY_CATEGORIES = [
  { label: "Response Time", score: 4.5 },
  { label: "Quality of Work", score: 4.7 },
  { label: "Communication", score: 4.6 },
  { label: "Professionalism", score: 4.8 },
  { label: "Overall Satisfaction", score: 4.6 },
];

const SURVEY_MONTHLY = [
  { month: "Jan", score: 4.4, responses: 38 },
  { month: "Feb", score: 4.6, responses: 41 },
  { month: "Mar", score: 4.7, responses: 42 },
];

// ─── Shared Chart Components ──────────────────────────────────────────────────

interface HorizBarProps {
  data: { label: string; value: number; color: string; suffix?: string }[];
  height?: number;
}

const HorizBarChart = ({ data, height = 32 }: HorizBarProps) => {
  const max = Math.max(...data.map((d) => d.value));
  return (
    <HorizBarList>
      {data.map((d) => (
        <HorizBarRow key={d.label}>
          <HorizBarLabel>{d.label}</HorizBarLabel>
          <HorizBarTrack>
            <HorizBarFill
              style={{
                width: `${(d.value / max) * 100}%`,
                background: d.color,
                height,
              }}
            />
          </HorizBarTrack>
          <HorizBarValue>
            {d.value}
            {d.suffix ?? ""}
          </HorizBarValue>
        </HorizBarRow>
      ))}
    </HorizBarList>
  );
};

// D3 grouped vertical bar chart (used for Monthly & BMT sections)
interface GroupedBarChartProps {
  data: { label: string; values: { key: string; value: number; color: string }[] }[];
  yLabel?: string;
}

const GroupedBarChart = ({ data }: GroupedBarChartProps) => {
  const svgRef = useRef<SVGSVGElement>(null);

  useEffect(() => {
    if (!svgRef.current || data.length === 0) return;

    const margin = { top: 16, right: 16, bottom: 36, left: 40 };
    const totalWidth = 480;
    const totalHeight = 180;
    const w = totalWidth - margin.left - margin.right;
    const h = totalHeight - margin.top - margin.bottom;

    d3.select(svgRef.current).selectAll("*").remove();

    const svg = d3
      .select(svgRef.current)
      .attr("width", totalWidth)
      .attr("height", totalHeight)
      .attr("viewBox", `0 0 ${totalWidth} ${totalHeight}`)
      .attr("style", "max-width:100%;height:auto;")
      .append("g")
      .attr("transform", `translate(${margin.left},${margin.top})`);

    const groups = data.map((d) => d.label);
    const keys = data[0].values.map((v) => v.key);

    const x0 = d3.scaleBand().domain(groups).range([0, w]).padding(0.28);
    const x1 = d3.scaleBand().domain(keys).range([0, x0.bandwidth()]).padding(0.08);
    const maxVal = d3.max(data, (d) => d3.max(d.values, (v) => v.value)) ?? 0;
    const y = d3
      .scaleLinear()
      .domain([0, maxVal * 1.2])
      .range([h, 0]);

    // grid
    svg
      .append("g")
      .call(
        d3
          .axisLeft(y)
          .ticks(4)
          .tickSize(-w)
          .tickFormat(() => ""),
      )
      .selectAll("line")
      .style("stroke", "rgba(136,135,128,0.15)")
      .style("stroke-dasharray", "3,3");
    svg.select(".domain").remove();

    // x-axis
    svg
      .append("g")
      .attr("transform", `translate(0,${h})`)
      .call(d3.axisBottom(x0).tickSize(0))
      .select(".domain")
      .remove();
    svg.selectAll(".tick text").style("font-size", "11px").style("fill", "#888780");

    // y-axis
    svg
      .append("g")
      .call(d3.axisLeft(y).ticks(4).tickFormat(d3.format("d")))
      .selectAll("text")
      .style("font-size", "10px")
      .style("fill", "#888780");
    svg.selectAll(".domain").remove();

    // bars
    const colorMap: Record<string, string> = {};
    data[0].values.forEach((v) => {
      colorMap[v.key] = v.color;
    });

    const grp = svg
      .selectAll(".grp")
      .data(data)
      .enter()
      .append("g")
      .attr("transform", (d) => `translate(${x0(d.label)},0)`);

    grp
      .selectAll("rect")
      .data((d) => d.values)
      .enter()
      .append("rect")
      .attr("x", (d) => x1(d.key) ?? 0)
      .attr("y", h)
      .attr("width", x1.bandwidth())
      .attr("height", 0)
      .attr("fill", (d) => colorMap[d.key])
      .attr("rx", 3)
      .style("opacity", 0.9)
      .transition()
      .duration(600)
      .delay((_, i) => i * 60)
      .attr("y", (d) => y(d.value))
      .attr("height", (d) => h - y(d.value));
  }, [data]);

  return <svg ref={svgRef} />;
};

// D3 horizontal bar chart (Service Types)
interface D3HorizChartProps {
  data: { name: string; count: number; color: string }[];
}

const D3HorizChart = ({ data }: D3HorizChartProps) => {
  const svgRef = useRef<SVGSVGElement>(null);

  useEffect(() => {
    if (!svgRef.current) return;

    const sorted = [...data].sort((a, b) => b.count - a.count);
    const margin = { top: 8, right: 60, bottom: 8, left: 140 };
    const rowH = 28;
    const totalWidth = 520;
    const totalHeight = sorted.length * rowH + margin.top + margin.bottom;
    const w = totalWidth - margin.left - margin.right;

    d3.select(svgRef.current).selectAll("*").remove();

    const svg = d3
      .select(svgRef.current)
      .attr("width", totalWidth)
      .attr("height", totalHeight)
      .attr("viewBox", `0 0 ${totalWidth} ${totalHeight}`)
      .attr("style", "max-width:100%;height:auto;")
      .append("g")
      .attr("transform", `translate(${margin.left},${margin.top})`);

    const maxCount = d3.max(sorted, (d) => d.count) ?? 0;
    const x = d3
      .scaleLinear()
      .domain([0, maxCount * 1.15])
      .range([0, w]);
    const y = d3
      .scaleBand()
      .domain(sorted.map((d) => d.name))
      .range([0, sorted.length * rowH])
      .padding(0.3);

    // y-axis labels
    svg.append("g").call(d3.axisLeft(y).tickSize(0)).select(".domain").remove();
    svg.selectAll(".tick text").style("font-size", "11px").style("fill", "#555555");

    // bars
    svg
      .selectAll(".bar")
      .data(sorted)
      .enter()
      .append("rect")
      .attr("class", "bar")
      .attr("x", 0)
      .attr("y", (d) => y(d.name) ?? 0)
      .attr("height", y.bandwidth())
      .attr("width", 0)
      .attr("fill", (d) => d.color)
      .attr("rx", 4)
      .style("opacity", 0.85)
      .transition()
      .duration(600)
      .delay((_, i) => i * 40)
      .attr("width", (d) => x(d.count));

    // value labels
    svg
      .selectAll(".lbl")
      .data(sorted)
      .enter()
      .append("text")
      .attr("class", "lbl")
      .attr("x", (d) => x(d.count) + 6)
      .attr("y", (d) => (y(d.name) ?? 0) + y.bandwidth() / 2 + 4)
      .attr("font-size", "11px")
      .attr("font-weight", "500")
      .attr("fill", "#888780")
      .style("opacity", 0)
      .text((d) => d.count)
      .transition()
      .delay((_, i) => i * 40 + 500)
      .duration(200)
      .style("opacity", 1);
  }, [data]);

  return <svg ref={svgRef} />;
};

// ─── Section: Date & Time ─────────────────────────────────────────────────────

interface DateTimeSectionProps {
  summary: GetReportingDataResponse["summary"];
  avgCompletionByPriority: GetReportingDataResponse["avgCompletionByPriority"];
  slaByPriority: GetReportingDataResponse["slaByPriority"];
}

const DateTimeSection = ({ summary, avgCompletionByPriority, slaByPriority }: DateTimeSectionProps) => {
  const completionData = avgCompletionByPriority.map((d) => ({
    label: d.priority,
    value: d.avgHrs,
    color: d.color,
    suffix: " hrs",
  }));

  return (
    <SectionWrap>
      <MetricRow>
        <MetricCard $accent="#1D9E75">
          <MetricValue style={{ color: "#1D9E75" }}>{summary.slaCompliance}%</MetricValue>
          <MetricLabel>SLA Compliance</MetricLabel>
        </MetricCard>
        <MetricCard $accent="#FF7A00">
          <MetricValue style={{ color: "#FF7A00" }}>{summary.avgCompletionHrs} hrs</MetricValue>
          <MetricLabel>Avg. Completion Time</MetricLabel>
        </MetricCard>
        <MetricCard $accent="#639922">
          <MetricValue style={{ color: "#639922" }}>{summary.completedWorkOrders}</MetricValue>
          <MetricLabel>Work Orders Completed</MetricLabel>
        </MetricCard>
      </MetricRow>

      <ChartGrid2>
        <Card style={{ gridColumn: "1 / -1" }}>
          <CardTitle>Avg. Completion Time by Priority</CardTitle>
          <SubNote>Hours from submission to completion</SubNote>
          <HorizBarChart data={completionData} height={24} />
        </Card>
      </ChartGrid2>

      <Card style={{ marginTop: 12 }}>
        <CardTitle>SLA Compliance Summary</CardTitle>
        <SlaTable>
          <thead>
            <SlaRow>
              <SlaHead>Priority</SlaHead>
              <SlaHead>Work Orders</SlaHead>
              <SlaHead>Within SLA</SlaHead>
              <SlaHead>Compliance</SlaHead>
            </SlaRow>
          </thead>
          <tbody>
            {slaByPriority.map((r) => (
              <SlaRow key={r.priority}>
                <SlaCell>
                  <PriorityDot style={{ background: r.color }} />
                  {r.priority}
                </SlaCell>
                <SlaCell>{r.total}</SlaCell>
                <SlaCell>{r.withinSla}</SlaCell>
                <SlaCell>
                  <CompliancePill $ok={r.compliancePct >= 90}>{r.compliancePct.toFixed(1)}%</CompliancePill>
                </SlaCell>
              </SlaRow>
            ))}
          </tbody>
        </SlaTable>
      </Card>
    </SectionWrap>
  );
};

// ─── Section: Priority ────────────────────────────────────────────────────────

interface PrioritySectionProps {
  priorityData: GetReportingDataResponse["priorityData"];
  totalWorkOrders: number;
}

const PrioritySection = ({ priorityData, totalWorkOrders }: PrioritySectionProps) => {
  const priorityBarData = priorityData.map((d) => ({
    label: d.priority,
    value: d.count,
    color: d.color,
  }));

  return (
    <SectionWrap>
      <MetricRow>
        {priorityData.map((d) => (
          <MetricCard key={d.priority} $accent={d.color}>
            <MetricValue style={{ color: d.color }}>{d.count}</MetricValue>
            <MetricLabel>{d.priority}</MetricLabel>
            <MetricSub>{((d.count / totalWorkOrders) * 100).toFixed(1)}% of total</MetricSub>
          </MetricCard>
        ))}
      </MetricRow>

      <ChartGrid2>
        <Card>
          <CardTitle>Work Orders by Priority</CardTitle>
          <SubNote>Total {totalWorkOrders} work orders</SubNote>
          <HorizBarChart data={priorityBarData} height={26} />
        </Card>

        <Card>
          <CardTitle>Priority Distribution</CardTitle>
          <SubNote>Percentage breakdown</SubNote>
          <DonutWrap>
            <DonutSvg data={priorityData.map((d) => ({ label: d.priority, value: d.count, color: d.color }))} />
            <DonutLegend>
              {priorityData.map((d) => (
                <DonutLegendRow key={d.priority}>
                  <DonutDot style={{ background: d.color }} />
                  <DonutLegendLabel>{d.priority}</DonutLegendLabel>
                  <DonutLegendVal>{((d.count / totalWorkOrders) * 100).toFixed(1)}%</DonutLegendVal>
                </DonutLegendRow>
              ))}
            </DonutLegend>
          </DonutWrap>
        </Card>
      </ChartGrid2>
    </SectionWrap>
  );
};

// Small donut SVG component
interface DonutSlice {
  label: string;
  value: number;
  color: string;
}

const DonutSvg = ({ data }: { data: DonutSlice[] }) => {
  const svgRef = useRef<SVGSVGElement>(null);

  useEffect(() => {
    if (!svgRef.current) return;

    const size = 120;
    const radius = size / 2;
    const innerRadius = radius * 0.62;

    d3.select(svgRef.current).selectAll("*").remove();

    const svg = d3
      .select(svgRef.current)
      .attr("viewBox", `${-radius} ${-radius} ${size} ${size}`)
      .attr("style", "display:block;");

    const pie = d3
      .pie<DonutSlice>()
      .value((d) => d.value)
      .sort(null);
    const arc = d3
      .arc<d3.PieArcDatum<DonutSlice>>()
      .innerRadius(innerRadius)
      .outerRadius(radius - 2);

    svg
      .selectAll("path")
      .data(pie(data))
      .enter()
      .append("path")
      .attr("d", arc)
      .attr("fill", (d) => d.data.color)
      .attr("stroke", "#ffffff")
      .attr("stroke-width", 2);
  }, [data]);

  return <svg ref={svgRef} style={{ width: 120, height: 120, flexShrink: 0 }} />;
};

// ─── Section: BMT ─────────────────────────────────────────────────────────────

interface BmtSectionProps {
  bmtMonthly: GetReportingDataResponse["bmtMonthly"];
}

const BmtSection = ({ bmtMonthly: allBmtMonthly }: BmtSectionProps) => {
  const currentYear = String(new Date().getFullYear());
  const bmtMonthly = useMemo(
    () => allBmtMonthly.filter((d) => d.month.includes(currentYear)),
    [allBmtMonthly, currentYear],
  );
  const totalInternal = bmtMonthly.reduce((s, d) => s + d.internal, 0);
  const totalExternal = bmtMonthly.reduce((s, d) => s + d.external, 0);
  const internalPct = Math.round((totalInternal / (totalInternal + totalExternal)) * 100);

  const groupedData = useMemo(
    () =>
      bmtMonthly.map((d) => ({
        label: d.month,
        values: [
          { key: "Internal", value: d.internal, color: "#639922" },
          { key: "External", value: d.external, color: "#378ADD" },
        ],
      })),
    [bmtMonthly],
  );

  return (
    <SectionWrap>
      <MetricRow>
        <MetricCard $accent="#639922">
          <MetricValue style={{ color: "#639922" }}>{totalInternal}</MetricValue>
          <MetricLabel>Internal</MetricLabel>
          <MetricSub>{internalPct}% of total</MetricSub>
        </MetricCard>
        <MetricCard $accent="#378ADD">
          <MetricValue style={{ color: "#378ADD" }}>{totalExternal}</MetricValue>
          <MetricLabel>External</MetricLabel>
          <MetricSub>{100 - internalPct}% of total</MetricSub>
        </MetricCard>
        <MetricCard $accent="#888780">
          <MetricValue>{totalInternal + totalExternal}</MetricValue>
          <MetricLabel>Total Work Orders</MetricLabel>
        </MetricCard>
      </MetricRow>

      <ChartGrid2>
        <Card>
          <CardTitle>Internal vs. External — Monthly</CardTitle>
          <SubNote>Work orders completed by delivery type</SubNote>
          <GroupedBarChart data={groupedData} />
          <LegendRow>
            <LegendItem>
              <LegendDot style={{ background: "#639922" }} />
              Internal
            </LegendItem>
            <LegendItem>
              <LegendDot style={{ background: "#378ADD" }} />
              External
            </LegendItem>
          </LegendRow>
        </Card>

        <Card>
          <CardTitle>Delivery Split by Month</CardTitle>
          <SubNote>Percentage of work internally performed</SubNote>
          <BmtSplitTable>
            <thead>
              <BmtRow>
                <BmtHead>Month</BmtHead>
                <BmtHead>Internal</BmtHead>
                <BmtHead>External</BmtHead>
                <BmtHead>Internal %</BmtHead>
              </BmtRow>
            </thead>
            <tbody>
              {bmtMonthly.map((d) => {
                const total = d.internal + d.external;
                const pct = Math.round((d.internal / total) * 100);
                return (
                  <BmtRow key={d.month}>
                    <BmtCell>{d.month}</BmtCell>
                    <BmtCell>{d.internal}</BmtCell>
                    <BmtCell>{d.external}</BmtCell>
                    <BmtCell>
                      <ProgressWrap>
                        <ProgressBar style={{ width: `${pct}%`, background: "#639922" }} />
                      </ProgressWrap>
                      <PctText>{pct}%</PctText>
                    </BmtCell>
                  </BmtRow>
                );
              })}
            </tbody>
          </BmtSplitTable>
        </Card>
      </ChartGrid2>
    </SectionWrap>
  );
};

// ─── Section: Monthly ─────────────────────────────────────────────────────────

interface MonthlySectionProps {
  monthlyData: GetReportingDataResponse["monthlyData"];
  summary: GetReportingDataResponse["summary"];
}

const MonthlySection = ({ monthlyData: allMonthlyData, summary }: MonthlySectionProps) => {
  const currentYear = String(new Date().getFullYear());
  const monthlyData = useMemo(
    () => allMonthlyData.filter((d) => d.month.includes(currentYear)),
    [allMonthlyData, currentYear],
  );
  const totalCompleted = monthlyData.reduce((s, d) => s + d.completed, 0);
  const totalOpen = monthlyData.reduce((s, d) => s + d.open, 0);

  const groupedData = useMemo(
    () =>
      monthlyData.map((d) => ({
        label: d.month,
        values: [
          { key: "Completed", value: d.completed, color: "#639922" },
          { key: "Open", value: d.open, color: "#378ADD" },
        ],
      })),
    [monthlyData],
  );

  return (
    <SectionWrap>
      <MetricRow>
        {monthlyData.map((d) => (
          <MetricCard key={d.month} $accent="#639922">
            <MetricValue>{d.total}</MetricValue>
            <MetricLabel>{d.month} — Total</MetricLabel>
            <MetricSub>
              {d.completed} completed · {d.open} open
            </MetricSub>
          </MetricCard>
        ))}
        <MetricCard $accent="#888780">
          <MetricValue>{summary.totalWorkOrders}</MetricValue>
          <MetricLabel>Overall Total</MetricLabel>
          <MetricSub>{summary.completedWorkOrders} completed</MetricSub>
        </MetricCard>
      </MetricRow>

      <Card>
        <CardTitle>Monthly Work Order Volume</CardTitle>
        <SubNote>Completed vs. open by month</SubNote>
        <GroupedBarChart data={groupedData} />
        <LegendRow>
          <LegendItem>
            <LegendDot style={{ background: "#639922" }} />
            Completed
          </LegendItem>
          <LegendItem>
            <LegendDot style={{ background: "#378ADD" }} />
            Open
          </LegendItem>
        </LegendRow>
      </Card>

      <Card style={{ marginTop: 12 }}>
        <CardTitle>Monthly Summary Table</CardTitle>
        <MonthlyTable>
          <thead>
            <MonthlyRow>
              <MonthlyHead>Month</MonthlyHead>
              <MonthlyHead>Total</MonthlyHead>
              <MonthlyHead>Completed</MonthlyHead>
              <MonthlyHead>Open</MonthlyHead>
              <MonthlyHead>Completion Rate</MonthlyHead>
            </MonthlyRow>
          </thead>
          <tbody>
            {monthlyData.map((d) => {
              const rate = Math.round((d.completed / d.total) * 100);
              return (
                <MonthlyRow key={d.month}>
                  <MonthlyCell>{d.month}</MonthlyCell>
                  <MonthlyCell>{d.total}</MonthlyCell>
                  <MonthlyCell>{d.completed}</MonthlyCell>
                  <MonthlyCell>{d.open}</MonthlyCell>
                  <MonthlyCell>
                    <ProgressWrap>
                      <ProgressBar style={{ width: `${rate}%`, background: "#639922" }} />
                    </ProgressWrap>
                    <PctText>{rate}%</PctText>
                  </MonthlyCell>
                </MonthlyRow>
              );
            })}
            <MonthlyRow>
              <MonthlyFooter>Period Total</MonthlyFooter>
              <MonthlyFooter>{totalCompleted + totalOpen}</MonthlyFooter>
              <MonthlyFooter>{totalCompleted}</MonthlyFooter>
              <MonthlyFooter>{totalOpen}</MonthlyFooter>
              <MonthlyFooter>{Math.round((totalCompleted / (totalCompleted + totalOpen)) * 100)}%</MonthlyFooter>
            </MonthlyRow>
          </tbody>
        </MonthlyTable>
      </Card>
    </SectionWrap>
  );
};

// ─── Section: Service Types ───────────────────────────────────────────────────

interface ServiceTypesSectionProps {
  workTypeData: GetReportingDataResponse["workTypeData"];
}

const ServiceTypesSection = ({ workTypeData }: ServiceTypesSectionProps) => {
  const enriched = useMemo(
    () => workTypeData.map((d, i) => ({ ...d, color: getCategoryColor(d.name, i) })),
    [workTypeData],
  );
  const sorted = [...enriched].sort((a, b) => b.count - a.count);
  const totalServiceWOs = enriched.reduce((s, d) => s + d.count, 0);
  const topService = sorted[0];

  return (
    <SectionWrap>
      <MetricRow>
        <MetricCard $accent="#639922">
          <MetricValue>{enriched.length}</MetricValue>
          <MetricLabel>Service Categories</MetricLabel>
        </MetricCard>
        <MetricCard $accent="#378ADD">
          <MetricValue>{totalServiceWOs}</MetricValue>
          <MetricLabel>Total Work Orders</MetricLabel>
        </MetricCard>
        {topService && (
          <MetricCard $accent="#FF7A00">
            <MetricValue>{topService.name}</MetricValue>
            <MetricLabel>Most Common Service</MetricLabel>
            <MetricSub>{topService.count} work orders</MetricSub>
          </MetricCard>
        )}
      </MetricRow>

      <ChartGrid2>
        <Card>
          <CardTitle>Work Orders by Service Type</CardTitle>
          <SubNote>Sorted by volume</SubNote>
          <D3HorizChart data={enriched} />
        </Card>

        <Card>
          <CardTitle>Service Type Breakdown</CardTitle>
          <SubNote>Count and percentage of total</SubNote>
          <ServiceTable>
            <thead>
              <ServiceRow>
                <ServiceHead>Service Type</ServiceHead>
                <ServiceHead>WOs</ServiceHead>
                <ServiceHead>% Share</ServiceHead>
              </ServiceRow>
            </thead>
            <tbody>
              {sorted.map((d) => (
                <ServiceRow key={d.name}>
                  <ServiceCell>
                    <ServiceDot style={{ background: d.color }} />
                    {d.name}
                  </ServiceCell>
                  <ServiceCell>{d.count}</ServiceCell>
                  <ServiceCell>
                    <ProgressWrap>
                      <ProgressBar
                        style={{
                          width: `${(d.count / totalServiceWOs) * 100}%`,
                          background: d.color,
                        }}
                      />
                    </ProgressWrap>
                    <PctText>{((d.count / totalServiceWOs) * 100).toFixed(1)}%</PctText>
                  </ServiceCell>
                </ServiceRow>
              ))}
            </tbody>
          </ServiceTable>
        </Card>
      </ChartGrid2>
    </SectionWrap>
  );
};

// ─── Section: Survey Results ──────────────────────────────────────────────────

const StarScore = ({ score }: { score: number }) => {
  const full = Math.floor(score);
  const partial = score - full;
  return (
    <StarRow>
      {[1, 2, 3, 4, 5].map((i) => (
        <StarIcon key={i} $filled={i <= full || (i === full + 1 && partial >= 0.5)} />
      ))}
      <StarText>{score.toFixed(1)} / 5</StarText>
    </StarRow>
  );
};

const SurveySection = () => {
  const catData = SURVEY_CATEGORIES.map((d) => ({
    label: d.label,
    value: d.score,
    color: "#639922",
    suffix: "/5",
  }));

  return (
    <SectionWrap>
      <MetricRow>
        <MetricCard $accent="#639922" style={{ gridColumn: "span 1" }}>
          <MetricValue style={{ color: "#639922", fontSize: 28 }}>4.6</MetricValue>
          <StarScore score={4.6} />
          <MetricLabel>Overall CSAT Score</MetricLabel>
        </MetricCard>
        <MetricCard $accent="#378ADD">
          <MetricValue style={{ color: "#378ADD" }}>68%</MetricValue>
          <MetricLabel>Survey Response Rate</MetricLabel>
          <MetricSub>121 of 178 respondents</MetricSub>
        </MetricCard>
        <MetricCard $accent="#1D9E75">
          <MetricValue style={{ color: "#1D9E75" }}>92%</MetricValue>
          <MetricLabel>Would Recommend</MetricLabel>
          <MetricSub>Q1 2026</MetricSub>
        </MetricCard>
        <MetricCard $accent="#FF7A00">
          <MetricValue style={{ color: "#FF7A00" }}>{SURVEY_MONTHLY.reduce((s, d) => s + d.responses, 0)}</MetricValue>
          <MetricLabel>Total Responses</MetricLabel>
          <MetricSub>Q1 2026</MetricSub>
        </MetricCard>
      </MetricRow>

      <ChartGrid2>
        <Card>
          <CardTitle>Satisfaction by Category</CardTitle>
          <SubNote>Average score out of 5</SubNote>
          <HorizBarChart data={catData} height={22} />
        </Card>

        <Card>
          <CardTitle>Monthly Survey Trend</CardTitle>
          <SubNote>Average CSAT score and response count</SubNote>
          <SurveyTrendTable>
            <thead>
              <SurveyTrendRow>
                <SurveyTrendHead>Month</SurveyTrendHead>
                <SurveyTrendHead>Score</SurveyTrendHead>
                <SurveyTrendHead>Responses</SurveyTrendHead>
                <SurveyTrendHead>Rating</SurveyTrendHead>
              </SurveyTrendRow>
            </thead>
            <tbody>
              {SURVEY_MONTHLY.map((d) => (
                <SurveyTrendRow key={d.month}>
                  <SurveyTrendCell>{d.month} 2026</SurveyTrendCell>
                  <SurveyTrendCell>
                    <ScoreChip>{d.score.toFixed(1)}</ScoreChip>
                  </SurveyTrendCell>
                  <SurveyTrendCell>{d.responses}</SurveyTrendCell>
                  <SurveyTrendCell>
                    <StarScore score={d.score} />
                  </SurveyTrendCell>
                </SurveyTrendRow>
              ))}
            </tbody>
          </SurveyTrendTable>
        </Card>
      </ChartGrid2>
    </SectionWrap>
  );
};

// ─── Tab navigation ───────────────────────────────────────────────────────────

type TabId = "datetime" | "priority" | "bmt" | "monthly" | "servicetypes" | "survey";

const TABS: { id: TabId; label: string; icon: string }[] = [
  { id: "datetime", label: "Date & Time", icon: "ti-clock" },
  { id: "priority", label: "Priority", icon: "ti-alert-triangle" },
  { id: "bmt", label: "BMT", icon: "ti-users" },
  { id: "monthly", label: "Monthly", icon: "ti-calendar" },
  { id: "servicetypes", label: "Service Types", icon: "ti-list" },
  { id: "survey", label: "Survey Results", icon: "ti-star" },
];

// ─── Top KPI summary data ─────────────────────────────────────────────────────

// ─── Main Component ───────────────────────────────────────────────────────────

function Reporting() {
  const dispatch = useDispatch();
  const [activeTab, setActiveTab] = useState<TabId>("datetime");
  const [reportingData, setReportingData] = useState<GetReportingDataResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    dispatch(setPageNameHeader("Reporting"));
    FirebaseDataService.getReportingData()
      .then(({ data }) => setReportingData(data))
      .catch((err) => console.error("Failed to load reporting data:", err))
      .finally(() => setIsLoading(false));
    return () => {
      dispatch(setPageNameHeader(""));
    };
  }, [dispatch]);

  const summary = reportingData?.summary;
  const topKpis = summary
    ? [
        {
          label: "Total Work Orders",
          value: summary.totalWorkOrders,
          sub: "all time",
          icon: "ti-clipboard-list",
          iconBg: "#EAF3DE",
          iconColor: "#3B6D11",
          accentColor: "#639922",
        },
        {
          label: "Completed",
          value: summary.completedWorkOrders,
          sub: `${Math.round((summary.completedWorkOrders / summary.totalWorkOrders) * 100)}% completion rate`,
          icon: "ti-circle-check",
          iconBg: "#E1F5EE",
          iconColor: "#0F6E56",
          accentColor: "#1D9E75",
        },
        {
          label: "SLA Compliance",
          value: `${summary.slaCompliance}%`,
          sub: "within target",
          icon: "ti-shield-check",
          iconBg: "#E6F1FB",
          iconColor: "#185FA5",
          accentColor: "#378ADD",
        },
        {
          label: "Avg. Completion",
          value: `${summary.avgCompletionHrs} hrs`,
          sub: "all priorities",
          icon: "ti-hourglass",
          iconBg: "#FFF3E6",
          iconColor: "#A85500",
          accentColor: "#FF7A00",
        },
      ]
    : [];

  return (
    <Content>
      {/* KPI summary row */}
      <KpiRow>
        {topKpis.map((kpi) => (
          <KpiCard key={kpi.label} {...kpi} />
        ))}
      </KpiRow>

      {isLoading ? (
        <LoadingWrap>
          <Spinner animation="border" style={{ color: "#639922" }} />
        </LoadingWrap>
      ) : !reportingData ? (
        <LoadingWrap>Failed to load reporting data.</LoadingWrap>
      ) : (
        <>
          {/* Tab navigation */}
          <TabBar>
            {TABS.map((tab) => (
              <TabBtn key={tab.id} $active={activeTab === tab.id} onClick={() => setActiveTab(tab.id)}>
                <i className={`ti ${tab.icon}`} style={{ fontSize: 13 }} />
                {tab.label}
              </TabBtn>
            ))}
          </TabBar>

          {/* Section content */}
          <div key={activeTab}>
            {activeTab === "datetime" && (
              <DateTimeSection
                summary={reportingData.summary}
                avgCompletionByPriority={reportingData.avgCompletionByPriority}
                slaByPriority={reportingData.slaByPriority}
              />
            )}
            {activeTab === "priority" && (
              <PrioritySection
                priorityData={reportingData.priorityData}
                totalWorkOrders={reportingData.summary.totalWorkOrders}
              />
            )}
            {activeTab === "bmt" && <BmtSection bmtMonthly={reportingData.bmtMonthly} />}
            {activeTab === "monthly" && (
              <MonthlySection monthlyData={reportingData.monthlyData} summary={reportingData.summary} />
            )}
            {activeTab === "servicetypes" && <ServiceTypesSection workTypeData={reportingData.workTypeData} />}
            {activeTab === "survey" && <SurveySection />}
          </div>
        </>
      )}
    </Content>
  );
}

export default Reporting;

// ─── KPI Card component ───────────────────────────────────────────────────────

const KpiCard = ({
  label,
  value,
  sub,
  icon,
  iconBg,
  iconColor,
  accentColor,
}: {
  label: string;
  value: number | string;
  sub: string;
  icon: string;
  iconBg: string;
  iconColor: string;
  accentColor: string;
}) => (
  <KpiCardWrap>
    <KpiTop>
      <KpiIcon style={{ background: iconBg }}>
        <i className={`ti ${icon}`} style={{ fontSize: 17, color: iconColor }} />
      </KpiIcon>
      <KpiLabel>{label}</KpiLabel>
    </KpiTop>
    <KpiValue>{value}</KpiValue>
    <KpiSub>{sub}</KpiSub>
    <KpiStripe style={{ background: accentColor }} />
  </KpiCardWrap>
);

// ─── Styled Components ────────────────────────────────────────────────────────

const LoadingWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 80px 0;
  font-size: 14px;
  color: #888780;
`;

const KpiRow = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
  margin-bottom: 12px;
`;

const KpiCardWrap = styled.div`
  background: #ffffff;
  border: 0.5px solid rgba(0, 0, 0, 0.12);
  border-radius: 12px;
  padding: 16px 18px;
  display: flex;
  flex-direction: column;
  gap: 3px;
  position: relative;
  overflow: hidden;
`;

const KpiTop = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
`;

const KpiIcon = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const KpiLabel = styled.span`
  font-size: 11px;
  font-weight: 500;
  color: ${colors.grey.dark};
  letter-spacing: 0.04em;
`;

const KpiValue = styled.div`
  font-size: 26px;
  font-weight: 700;
  color: #1a1a1a;
  line-height: 1.1;
`;

const KpiSub = styled.div`
  font-size: 11px;
  color: ${colors.grey.dark5};
`;

const KpiStripe = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 3px;
`;

// const BadgeRow = styled.div`
//   display: flex;
//   align-items: center;
//   margin-bottom: 8px;
// `;

// const StaticBadge = styled.span`
//   font-size: 10px;
//   font-weight: 600;
//   color: ${colors.grey.dark5};
//   background: ${colors.grey.light3};
//   border-radius: 20px;
//   padding: 2px 10px;
//   letter-spacing: 0.04em;
// `;

const TabBar = styled.div`
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  margin-bottom: 14px;
`;

const TabBtn = styled.button<{ $active: boolean }>`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 500;
  padding: 6px 14px;
  border-radius: 8px;
  border: 1px solid ${({ $active }) => ($active ? "transparent" : colors.grey.light1)};
  background: ${({ $active }) => ($active ? "#639922" : "#ffffff")};
  color: ${({ $active }) => ($active ? "#ffffff" : colors.grey.dark)};
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background: ${({ $active }) => ($active ? "#639922" : colors.grey.light2)};
  }
`;

const SectionWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
`;

const MetricRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 10px;
  margin-bottom: 12px;
`;

const MetricCard = styled.div<{ $accent: string }>`
  background: #ffffff;
  border: 0.5px solid rgba(0, 0, 0, 0.12);
  border-radius: 12px;
  padding: 14px 16px;
  position: relative;
  overflow: hidden;

  &::after {
    content: "";
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: ${({ $accent }) => $accent};
  }
`;

const MetricValue = styled.div`
  font-size: 24px;
  font-weight: 700;
  color: #1a1a1a;
  line-height: 1.15;
  margin-bottom: 2px;
`;

const MetricLabel = styled.div`
  font-size: 11px;
  font-weight: 500;
  color: ${colors.grey.dark};
  letter-spacing: 0.04em;
  text-transform: uppercase;
`;

const MetricSub = styled.div`
  font-size: 10px;
  color: ${colors.grey.dark5};
  margin-top: 2px;
`;

const Card = styled.div`
  background: #ffffff;
  border: 0.5px solid rgba(0, 0, 0, 0.12);
  border-radius: 12px;
  padding: 16px;
`;

const CardTitle = styled.div`
  font-size: 11px;
  font-weight: 500;
  color: ${colors.grey.dark};
  letter-spacing: 0.06em;
  text-transform: uppercase;
  margin-bottom: 2px;
`;

const SubNote = styled.div`
  font-size: 10px;
  color: ${colors.grey.dark5};
  margin-bottom: 12px;
`;

const ChartGrid2 = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-bottom: 12px;
`;

// Horizontal bar chart styles
const HorizBarList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const HorizBarRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const HorizBarLabel = styled.span`
  font-size: 11px;
  color: ${colors.grey.dark};
  width: 90px;
  flex-shrink: 0;
`;

const HorizBarTrack = styled.div`
  flex: 1;
  background: ${colors.grey.light3};
  border-radius: 4px;
  overflow: hidden;
`;

const HorizBarFill = styled.div`
  border-radius: 4px;
  transition: width 0.6s ease;
`;

const HorizBarValue = styled.span`
  font-size: 11px;
  font-weight: 600;
  color: ${colors.grey.dark};
  min-width: 52px;
  text-align: right;
`;

// SLA table
const SlaTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
`;

const SlaRow = styled.tr`
  border-bottom: 0.5px solid ${colors.grey.light3};
`;

const SlaHead = styled.th`
  text-align: left;
  font-size: 10px;
  font-weight: 600;
  color: ${colors.grey.dark5};
  text-transform: uppercase;
  letter-spacing: 0.04em;
  padding: 6px 8px 6px 0;
`;

const SlaCell = styled.td`
  padding: 7px 8px 7px 0;
  color: ${colors.grey.dark};
  display: table-cell;
  vertical-align: middle;
`;

const PriorityDot = styled.span`
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-right: 6px;
  vertical-align: middle;
`;

const CompliancePill = styled.span<{ $ok: boolean }>`
  font-size: 10px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 20px;
  background: ${({ $ok }) => ($ok ? "#E1F5EE" : "#FCEBEB")};
  color: ${({ $ok }) => ($ok ? "#0F6E56" : "#A32D2D")};
`;

// Priority donut legend
const DonutWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
`;

const DonutLegend = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
`;

const DonutLegendRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const DonutDot = styled.span`
  width: 10px;
  height: 10px;
  border-radius: 3px;
  flex-shrink: 0;
`;

const DonutLegendLabel = styled.span`
  font-size: 11px;
  color: ${colors.grey.dark};
  flex: 1;
`;

const DonutLegendVal = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: #1a1a1a;
`;

// Legend (grouped bar chart)
const LegendRow = styled.div`
  display: flex;
  gap: 16px;
  margin-top: 8px;
`;

const LegendItem = styled.span`
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 11px;
  color: ${colors.grey.dark};
`;

const LegendDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 2px;
  flex-shrink: 0;
`;

// Progress bar (inline)
const ProgressWrap = styled.div`
  display: inline-block;
  width: 60px;
  height: 6px;
  background: ${colors.grey.light3};
  border-radius: 3px;
  overflow: hidden;
  vertical-align: middle;
  margin-right: 6px;
`;

const ProgressBar = styled.div`
  height: 100%;
  border-radius: 3px;
  transition: width 0.6s ease;
`;

const PctText = styled.span`
  font-size: 11px;
  font-weight: 500;
  color: ${colors.grey.dark};
  vertical-align: middle;
`;

// BMT table
const BmtSplitTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
  margin-top: 8px;
`;

const BmtRow = styled.tr`
  border-bottom: 0.5px solid ${colors.grey.light3};
`;

const BmtHead = styled.th`
  text-align: left;
  font-size: 10px;
  font-weight: 600;
  color: ${colors.grey.dark5};
  text-transform: uppercase;
  letter-spacing: 0.04em;
  padding: 6px 8px 6px 0;
`;

const BmtCell = styled.td`
  padding: 7px 8px 7px 0;
  color: ${colors.grey.dark};
  vertical-align: middle;
`;

// Monthly table
const MonthlyTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
  margin-top: 8px;
`;

const MonthlyRow = styled.tr`
  border-bottom: 0.5px solid ${colors.grey.light3};
`;

const MonthlyHead = styled.th`
  text-align: left;
  font-size: 10px;
  font-weight: 600;
  color: ${colors.grey.dark5};
  text-transform: uppercase;
  letter-spacing: 0.04em;
  padding: 6px 8px 6px 0;
`;

const MonthlyCell = styled.td`
  padding: 7px 8px 7px 0;
  color: ${colors.grey.dark};
  vertical-align: middle;
`;

const MonthlyFooter = styled.td`
  padding: 8px 8px 8px 0;
  font-weight: 600;
  font-size: 12px;
  color: #1a1a1a;
  border-top: 1px solid ${colors.grey.light1};
  vertical-align: middle;
`;

// Service types
const ServiceTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
  margin-top: 8px;
`;

const ServiceRow = styled.tr`
  border-bottom: 0.5px solid ${colors.grey.light3};
`;

const ServiceHead = styled.th`
  text-align: left;
  font-size: 10px;
  font-weight: 600;
  color: ${colors.grey.dark5};
  text-transform: uppercase;
  letter-spacing: 0.04em;
  padding: 6px 8px 6px 0;
`;

const ServiceCell = styled.td`
  padding: 7px 8px 7px 0;
  color: ${colors.grey.dark};
  vertical-align: middle;
`;

const ServiceDot = styled.span`
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 2px;
  margin-right: 6px;
  vertical-align: middle;
`;

// Survey
const StarRow = styled.span`
  display: flex;
  align-items: center;
  gap: 3px;
  margin: 2px 0;
`;

const StarIcon = styled.span<{ $filled: boolean }>`
  width: 12px;
  height: 12px;
  clip-path: polygon(50% 0%, 61% 35%, 98% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 2% 35%, 39% 35%);
  background: ${({ $filled }) => ($filled ? "#F5C518" : colors.grey.light3)};
`;

const StarText = styled.span`
  font-size: 11px;
  font-weight: 600;
  color: ${colors.grey.dark};
  margin-left: 4px;
`;

const SurveyTrendTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
  margin-top: 8px;
`;

const SurveyTrendRow = styled.tr`
  border-bottom: 0.5px solid ${colors.grey.light3};
`;

const SurveyTrendHead = styled.th`
  text-align: left;
  font-size: 10px;
  font-weight: 600;
  color: ${colors.grey.dark5};
  text-transform: uppercase;
  letter-spacing: 0.04em;
  padding: 6px 8px 6px 0;
`;

const SurveyTrendCell = styled.td`
  padding: 8px 8px 8px 0;
  color: ${colors.grey.dark};
  vertical-align: middle;
`;

const ScoreChip = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #eaf3de;
  color: #3b6d11;
  font-size: 11px;
  font-weight: 700;
  border-radius: 20px;
  padding: 2px 10px;
`;
